/**
 * 投放神獸：點地面放下當前配方；Shift 刪最近一隻。
 */

import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { useGameStore } from '../core/store/gameStore'
import { WATER_LEVEL } from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

export function PlaceBeastController() {
  const { camera, gl } = useThree()
  const placing = useGameStore(s => s.placingBeast)
  const visitMode = useGameStore(s => s.visitMode)
  const makerKind = useGameStore(s => s.makerKind)
  const pointer = useRef(new THREE.Vector2())
  const hit = useRef(new THREE.Vector3())
  const hasHit = useRef(false)
  const down = useRef<{ x: number; y: number } | null>(null)
  const ground = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), -WATER_LEVEL), [])
  const ray = useMemo(() => new THREE.Raycaster(), [])
  const ring = useRef<THREE.Mesh>(null)

  useEffect(() => {
    const el = gl.domElement
    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      pointer.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
      pointer.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    }
    const onDown = (e: PointerEvent) => {
      if (e.button === 0) down.current = { x: e.clientX, y: e.clientY }
    }
    const onUp = (e: PointerEvent) => {
      if (e.button !== 0 || !down.current || visitMode) return
      const dist = Math.hypot(e.clientX - down.current.x, e.clientY - down.current.y)
      down.current = null
      if (dist > 5 || !hasHit.current) return
      const st = useGameStore.getState()
      if (e.shiftKey) {
        let best = ''
        let bestD = 18
        for (const p of st.placements) {
          const d = Math.hypot(p.position[0] - hit.current.x, p.position[2] - hit.current.z)
          if (d < bestD) {
            bestD = d
            best = p.id
          }
        }
        if (best) st.removePlacement(best)
        return
      }
      if (!st.placingBeast) return
      const src = st.makerKind === 'plant' ? st.plantDraft : st.draft
      const species = { ...src, id: `sp:${Date.now().toString(36)}` }
      st.upsertSpecies(species)
      st.addPlacement({
        id: `pl:${Date.now().toString(36)}`,
        speciesId: species.id,
        position: [hit.current.x, hit.current.y, hit.current.z],
      })
      if (st.makerKind === 'plant') st.setPlantDraft({ id: `sp:${Date.now().toString(36)}p` })
      else st.setDraft({ id: `sp:${Date.now().toString(36)}` })
      st.setPlacingBeast(false)
      st.setEncounterToast(st.makerKind === 'plant' ? `已種下「${species.name}」` : `已投放「${species.name}」`)
    }
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointerup', onUp)
    return () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointerup', onUp)
    }
  }, [gl.domElement, visitMode])

  useFrame(() => {
    ray.setFromCamera(pointer.current, camera)
    const pt = ray.ray.intersectPlane(ground, hit.current)
    if (!pt) {
      hasHit.current = false
      return
    }
    hit.current.y = Math.max(terrainBuffer.getHeight(hit.current.x, hit.current.z), WATER_LEVEL)
    hasHit.current = true
    if (ring.current) {
      ring.current.position.set(hit.current.x, hit.current.y + 0.35, hit.current.z)
    }
  })

  return placing && !visitMode ? (
    <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[2.2, 2.6, 24]} />
      <meshBasicMaterial color={makerKind === 'plant' ? '#4a7a5c' : '#a83a32'} transparent opacity={0.35} />
    </mesh>
  ) : null
}
